/**
 * "Which friend?" — the one tap between the menu and a per-friend page.
 *
 * Compare and Trade are the two destinations in the app that cannot be reached
 * by naming them: both are about a PAIR of trainers, so a menu entry for either
 * is only half an instruction. Rather than drop the trainer on the Friends list
 * and hope they work out that a row is tappable, the menu asks the question
 * directly and takes one tap for an answer.
 *
 * FACES, NOT A LIST. A trainer knows their friends by monogram and colour — the
 * same avatar the Friends list, Compare and every trade surface already draw
 * them with — so the grid is scannable in a way a column of names is not, and a
 * pick is one tap rather than a scroll and a tap.
 *
 * With no friends yet there is nothing to ask, so the dialog says so and offers
 * the only useful action instead.
 */
import { useLocation } from "wouter";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Monogram } from "@/pages/Friends";
import { useFriendChoices } from "@/lib/useFriendIdentity";

export function FriendPickerDialog({
  open,
  onOpenChange,
  title,
  description,
  onPick,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  /** Handed the chosen friend's ACCOUNT id — what /friends/:id/* expects. */
  onPick: (accountId: number) => void;
}) {
  const [, setLocation] = useLocation();
  const { friends, isLoading } = useFriendChoices();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="friend-picker">
        <DialogHeader>
          <DialogTitle className="text-[15px]">{title}</DialogTitle>
          <DialogDescription className="text-[11.5px]">{description}</DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <p className="text-[11.5px] py-2" style={{ color: "var(--text-tertiary)" }} data-testid="friend-picker-loading">
            Loading your friends…
          </p>
        ) : friends.length === 0 ? (
          <div className="py-2" data-testid="friend-picker-empty">
            <p className="text-[11.5px] mb-3" style={{ color: "var(--text-tertiary)" }}>
              You haven't connected with anyone yet. Both of these pages are about you and
              somebody else, so they need a friend first.
            </p>
            <button
              type="button"
              data-testid="friend-picker-add"
              onClick={() => {
                onOpenChange(false);
                setLocation("/friends");
              }}
              className="w-full rounded-full py-2.5 text-[12px] font-bold"
              style={{ background: "var(--accent-fill)", color: "var(--text-on-accent)", minHeight: 44 }}
            >
              Add a friend
            </button>
          </div>
        ) : (
          <ul
            className="grid grid-cols-3 gap-3 max-h-[50dvh] overflow-y-auto py-1"
            data-testid="friend-picker-list"
          >
            {friends.map((f) => (
              <li key={f.accountId}>
                <button
                  type="button"
                  data-testid={`friend-picker-${f.accountId}`}
                  onClick={() => {
                    onOpenChange(false);
                    onPick(f.accountId);
                  }}
                  aria-label={f.name}
                  className="w-full flex flex-col items-center gap-1.5 rounded-xl p-2 hover-elevate"
                  style={{ minHeight: 44 }}
                >
                  <Monogram letter={f.monogram} hue={f.hue} size={44} />
                  {/* The name UNDER the face, because the face is what a trainer
                      recognises and the name is what confirms it. */}
                  <span
                    className="w-full truncate text-center text-[10px] font-semibold"
                    style={{ color: "var(--text-primary)" }}
                  >
                    {f.name}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
